import React, { Component } from "react";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import PersonOutlineRoundedIcon from "@material-ui/icons/PersonOutlineRounded";
import MailOutlineRoundedIcon from "@material-ui/icons/MailOutlineRounded";
import PhoneIphoneRoundedIcon from "@material-ui/icons/PhoneIphoneRounded";
import PlaceOutlinedIcon from "@material-ui/icons/PlaceOutlined";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import TextField from "@material-ui/core/TextField";
import { Checkbox, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import PasswordResetV2 from "./PasswordResetV2";
import MuiPhoneNumber from "material-ui-phone-number";
import { getCheckoutPhoneCountryOptions } from "./ContactEditor";
import { isValidCheckoutPhone } from "../domain/checkoutFlow";
import StorefrontActionButton from "../../Components/Storefront/ActionButton/StorefrontActionButton";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { setUser } from "../../store/actions/auth";
import AppleAuth from "../../Components/Profile/AppleAuth";
import GoogleAuth from "../../Components/Profile/GoogleAuth";
import {
  clearCheckoutSocialAuth,
  parseSocialAuthCallback,
  readCheckoutSocialAuth,
  restoreCheckoutSocialAuthScroll,
  sanitizeSocialAuthReturnTo,
} from "../../Components/Profile/socialAuthFlow";
import { checkoutV2Api } from "../api";
import { getAuthCapability } from "../domain/authCapability";
import { getCheckoutAuthError } from "../domain/authErrors";
import { accessibleThemeInk } from "../domain/themeContrast";
import "./CheckoutAuthOverlay.css";
import "../styles/typography.css";
import {
  closeCheckoutV2Auth,
  openCheckoutV2Auth,
} from "../state/actions";

export const buildPhoneOtpPayload = ({
  phone,
  mode,
  code = "",
  language,
}) => ({
  mode,
  password: "",
  code,
  language,
  phone: `${phone || ""}`.replace(/[\s()-]/g, "").trim(),
  countryDialCode: "",
  otp_only: true,
});

const AuthContainer = ({ embedded, children, ...props }) => embedded
  ? <section {...props.PaperProps} aria-labelledby={props["aria-labelledby"]} aria-describedby={props["aria-describedby"]}>{children}</section>
  : <Dialog {...props}>{children}</Dialog>;

export class CheckoutAuthOverlay extends Component {
  static defaultProps = { api: checkoutV2Api };

  state = {
    identifier: this.props.initialIdentifier || "",
    password: "",
    code: "",
    verificationRequired: false,
    loading: false,
    error: "",
    resendAt: 0,
    now: Date.now(),
    resettingPassword: false,
    optInMarketing: false,
    contactChannel: "Email",
  };

  socialCallbackCompleting = false;

  replaceAfterSocialAuth = (returnTo, flow) => {
    this.props.history.replace(returnTo);
    restoreCheckoutSocialAuthScroll(flow);
  };

  componentDidMount() {
    this.completePersistedSocialAuth();
    this.cooldownTimer = setInterval(() => {
      if (this.state.resendAt > this.state.now)
        this.setState({ now: Date.now() });
    }, 1000);
  }

  componentWillUnmount() {
    clearInterval(this.cooldownTimer);
    this.authRequestId = (this.authRequestId || 0) + 1;
  }

  completePersistedSocialAuth = async () => {
    // Profile cards share this form; the global overlay alone owns callbacks.
    if (this.props.embedded) return;
    if (this.socialCallbackCompleting) return;
    const flow = readCheckoutSocialAuth();
    if (!flow || this.props.history.location.pathname !== "/profile") return;
    const callback = parseSocialAuthCallback(
      this.props.history.location.search || window.location.search
    );
    if (!callback.present && !this.props.user) return;

    this.socialCallbackCompleting = true;
    const returnTo = sanitizeSocialAuthReturnTo(flow.returnTo);
    try {
      if (callback.present && !callback.successful) {
        clearCheckoutSocialAuth();
        this.replaceAfterSocialAuth(returnTo, flow);
        this.props.open({
          ...this.props.overlay,
          open: true,
          method: null,
          returnTo,
        });
        this.setState({ loading: false, error: "social_auth_failed" });
        return;
      }
      if (callback.token && !this.props.user) {
        const authenticatedUser = await this.props.setUser(callback.token);
        if (!authenticatedUser && !this.props.user)
          throw new Error("social_auth_failed");
        if (readCheckoutSocialAuth()) {
          clearCheckoutSocialAuth();
          this.props.close();
          this.replaceAfterSocialAuth(returnTo, flow);
        }
        return;
      }
      clearCheckoutSocialAuth();
      this.props.close();
      this.replaceAfterSocialAuth(returnTo, flow);
    } catch (error) {
      clearCheckoutSocialAuth();
      this.replaceAfterSocialAuth(returnTo, flow);
      this.props.open({
        ...this.props.overlay,
        open: true,
        method: null,
        returnTo,
      });
      this.setState({ loading: false, error: "social_auth_failed" });
    } finally {
      this.socialCallbackCompleting = false;
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.overlay?.open !== this.props.overlay?.open ||
        prevProps.overlay?.method !== this.props.overlay?.method ||
        prevProps.overlay?.mode !== this.props.overlay?.mode) {
      this.authRequestId = (this.authRequestId || 0) + 1;
      this.setState({
        identifier: "",
        password: "",
        code: "",
        verificationRequired: false,
        loading: false,
        error: "",
        resendAt: 0,
        resettingPassword: false,
        optInMarketing: false,
        contactChannel: "Email",
      });
    }
    if (!prevProps.user && this.props.user) {
      const flow = readCheckoutSocialAuth();
      // A profile sign-in must not be redirected by an unrelated, closed modal.
      if (!flow && !prevProps.overlay?.open) return;
      const returnTo = sanitizeSocialAuthReturnTo(
        flow?.returnTo || prevProps.overlay?.returnTo
      );
      if (flow) clearCheckoutSocialAuth();
      this.props.close();
      if (this.props.history.location.pathname !== returnTo)
        this.replaceAfterSocialAuth(returnTo, flow);
      return;
    }
    this.completePersistedSocialAuth();
  }

  selectMethod = (method) => {
    this.props.open({
      ...this.props.overlay,
      open: true,
      method,
    });
  };

  finishPasswordReset = async (token) => {
    const requestId = (this.authRequestId || 0) + 1;
    this.authRequestId = requestId;
    const user = await this.props.setUser(token);
    if (requestId !== this.authRequestId) return false;
    if (!user && !this.props.user) return false;
    this.props.close();
    this.props.history.replace(this.props.overlay.returnTo || "/checkout");
    return true;
  };

  changeIdentifier = () => {
    this.authRequestId = (this.authRequestId || 0) + 1;
    this.setState({ verificationRequired: false, code: "", error: "", loading: false });
  };

  showAuthError = (response = {}) => {
    const retrySeconds = Number(response?.retry_after_seconds);
    this.setState({
      loading: false,
      error: response && typeof response === "object" && !Array.isArray(response)
        ? response : "authentication_unavailable",
      ...(Number.isFinite(retrySeconds) && retrySeconds > 0 ? {
        resendAt: Date.now() + Math.min(retrySeconds, 900) * 1000,
        now: Date.now(),
      } : {}),
    });
  };

  submit = async ({ resend = false } = {}) => {
    if (this.state.loading || (resend && Date.now() < this.state.resendAt)) return;
    const { overlay, api, language } = this.props;
    const { identifier, password, code } = this.state;
    const verificationRequired = this.state.verificationRequired && !resend;
    if (overlay.method === "phone_otp" && !isValidCheckoutPhone(identifier.replace(/[\s()-]/g, ""))) {
      this.setState({ error: "contact_phone_invalid" });
      return;
    }
    const passwordRequired =
      overlay.method === "email" && !verificationRequired;
    if (!identifier.trim() || (passwordRequired && !password.trim())) {
      this.setState({ error: "missing_credentials" });
      return;
    }
    if (verificationRequired && code.trim().length !== 6) {
      this.setState({ error: "invalid_code" });
      return;
    }

    this.setState({ loading: true, error: "" });
    const requestId = (this.authRequestId || 0) + 1;
    this.authRequestId = requestId;
    const mode = overlay.method === "phone_otp" ? "continue"
      : resend ? 1 : overlay.mode === "signup" ? 0 : 1;
    const common = { mode, password, code, language };
    try {
      let response;
      if (overlay.method === "email") {
        const payload = { ...common, email: identifier.trim(), ...(mode === 0 && this.props.settings?.enable_dot ? {
          optInMarketing: this.state.optInMarketing,
          contactChannel: this.state.optInMarketing ? this.state.contactChannel : null,
        } : {}) };
        response = verificationRequired
          ? await api.verifyEmail(payload)
          : await api.authenticateEmail(payload);
      } else {
        const payload = buildPhoneOtpPayload({
          phone: identifier,
          mode,
          code,
          language,
        });
        response = verificationRequired
          ? await api.verifyPhone(payload)
          : await api.authenticatePhone(payload);
      }

      if (requestId !== this.authRequestId) return;
      if (!response?.success) {
        this.showAuthError(response);
        return;
      }
      if (response.verify && !response.token) {
        this.setState({
          verificationRequired: true, loading: false, code: "",
          resendAt: Date.now() + Math.max(1, Number(response.cooldown_seconds) || 60) * 1000,
          now: Date.now(),
        });
        return;
      }
      if (response.token) {
        const authenticatedUser = await this.props.setUser(response.token);
        if (requestId !== this.authRequestId) return;
        if (!authenticatedUser && !this.props.user) {
          this.setState({ loading: false, error: "authentication_failed" });
          return;
        }
        this.props.close();
        this.props.history.replace(overlay.returnTo || "/checkout");
        return;
      }
      this.showAuthError();
    } catch (error) {
      if (requestId !== this.authRequestId) return;
      this.showAuthError(error?.response?.data);
    }
  };

  renderProviderChoice(capability, arabic, compact = false) {
    const providerLabels = {
      email: arabic ? "البريد الإلكتروني" : "Email",
      phone_otp: arabic ? "رقم الهاتف" : "Phone",
      apple: "Apple",
      google: "Google",
    };
    return (
      <div className={`checkout-auth__providers${compact ? " checkout-auth__providers--compact" : ""}`}>
        {capability.providers.map((provider) => {
          if (provider === "apple")
            return (
              <AppleAuth
                key={provider}
                forceEnabled
                appearance="checkout-v2"
                compact={compact}
                disabled={this.state.loading}
                history={this.props.history}
                returnTo={sanitizeSocialAuthReturnTo(
                  this.props.overlay.returnTo
                )}
              />
            );
          if (provider === "google")
            return (
              <GoogleAuth
                key={provider}
                forceEnabled
                appearance="checkout-v2"
                compact={compact}
                disabled={this.state.loading}
                history={this.props.history}
                returnTo={sanitizeSocialAuthReturnTo(
                  this.props.overlay.returnTo
                )}
              />
            );
          return (
            <Button
              key={provider}
              className="checkout-auth__provider"
              disabled={this.state.loading}
              aria-label={`${arabic ? "متابعة باستخدام" : "Continue with"} ${providerLabels[provider] || provider}`}
              variant="outlined"
              onClick={() => this.selectMethod(provider)}
            >
              {provider === "email" ? <MailOutlineRoundedIcon /> : <PhoneIphoneRoundedIcon />}
              <span>{!compact && <>{arabic ? "متابعة باستخدام" : "Continue with"}{" "}</>}
              {providerLabels[provider] || provider}</span>
            </Button>
          );
        })}
      </div>
    );
  }

  render() {
    const {
      overlay = {},
      settings,
      source,
      user,
      slug,
      language,
    } = this.props;
    const arabic = language === "arabic";
    const capability = getAuthCapability({ settings, source, user, slug });
    if (!overlay.open || user || !capability.enabled) return null;
    const formMethod = ["email", "phone_otp"].includes(overlay.method);
    const emailMethod = overlay.method === "email";
    const alternativeProviders = capability.providers.filter((provider) => provider !== overlay.method);
    const signup = emailMethod && overlay.mode === "signup";
    const verification = this.state.verificationRequired;
    const title = verification
      ? emailMethod ? arabic ? "تأكيد بريدك الإلكتروني" : "Confirm your email" : arabic ? "تحقق من الرمز" : "Check your code"
      : emailMethod
        ? signup ? arabic ? "إنشاء حسابك" : "Create your account" : arabic ? "أهلاً بعودتك" : "Welcome back"
        : formMethod ? arabic ? "المتابعة برقم الهاتف" : "Continue with phone"
          : arabic ? "طلباتك أسهل مع حسابك" : "Make ordering easier";
    const description = verification
      ? emailMethod
        ? arabic ? "أكد بريدك الإلكتروني لإكمال تسجيل الدخول. أدخل الرمز المرسل إلى:" : "Verify your email to finish signing in. Enter the code sent to:"
        : arabic ? "أدخل الرمز المكوّن من ستة أرقام المرسل إلى:" : "Enter the six-digit code sent to:"
      : formMethod
        ? overlay.method === "phone_otp"
          ? arabic ? "سنرسل لك رمز تحقق. لا حاجة لكلمة مرور." : "We’ll send you a verification code. No password needed."
          : signup ? arabic ? "أدخل بريدك الإلكتروني واختر كلمة مرور للبدء." : "Enter your email and choose a password to get started."
            : arabic ? "سجّل الدخول للوصول إلى بياناتك وطلباتك." : "Sign in to find your details and orders in one place."
        : arabic ? "احفظ بياناتك لزيارتك القادمة. اختر طريقة المتابعة المناسبة لك." : "Save your details for next time. Choose how to continue.";
    const resendSeconds = Math.max(0, Math.ceil((this.state.resendAt - this.state.now) / 1000));
    const selectedCountry = (this.props.countries || []).find(
      (country) => `${country.id}` === `${this.props.address?.country}`,
    );
    const authError = getCheckoutAuthError(this.state.error, { language, method: overlay.method, signup, verification });

    return (
      <AuthContainer
        embedded={this.props.embedded}
        open
        onClose={capability.loginRequired ? undefined : this.props.close}
        disableBackdropClick={capability.loginRequired}
        disableEscapeKeyDown={capability.loginRequired}
        aria-labelledby="checkout-v2-auth-title"
        aria-describedby="checkout-v2-auth-description"
        PaperProps={{
          className: `checkout-auth storefront-v2-surface${this.props.embedded ? " checkout-auth--embedded" : ""}`,
          dir: arabic ? "rtl" : "ltr",
          style: { "--auth-accent": settings?.dark || "#0099cc", "--auth-ink": accessibleThemeInk(settings?.dark) },
        }}
      >
        <DialogContent className="checkout-auth__content">
          {this.state.resettingPassword ? <PasswordResetV2
            {...this.props}
            method="email"
            initialIdentifier={this.state.identifier}
            onAuthenticated={this.finishPasswordReset}
            onBack={(identifier) => this.setState({ resettingPassword: false, identifier, password: "", error: "" })}
          /> : <>
          <div className="checkout-auth__hero">
            <span className="checkout-auth__illustration" aria-hidden="true"><PersonOutlineRoundedIcon /></span>
            {!this.props.embedded && !capability.loginRequired && <IconButton className="checkout-auth__close" onClick={this.props.close}
              aria-label={arabic ? "إغلاق تسجيل الدخول" : "Close sign in"}><CloseRoundedIcon /></IconButton>}
            <DialogTitle disableTypography id="checkout-v2-auth-title" className="checkout-auth__title"><h2>{title}</h2></DialogTitle>
            <p id="checkout-v2-auth-description">{description}</p>
            {verification && <bdi className="checkout-auth__identifier">{this.state.identifier}</bdi>}
            {!formMethod && <ul className="checkout-auth__benefits">
              <li><PlaceOutlinedIcon />{arabic ? "عناوينك محفوظة" : "Saved addresses"}</li>
              <li><ReceiptOutlinedIcon />{arabic ? "طلباتك في مكان واحد" : "Orders in one place"}</li>
            </ul>}
          </div>
          {emailMethod && !verification &&
            <div className="checkout-auth__modes" role="group" aria-label={arabic ? "حساب البريد الإلكتروني" : "Email account access"}>
              <Button disabled={this.state.loading} aria-pressed={!signup} onClick={() => this.props.open({ ...overlay, mode: "login" })}>{arabic ? "تسجيل الدخول" : "Sign in"}</Button>
              <Button disabled={this.state.loading} aria-pressed={signup} onClick={() => this.props.open({ ...overlay, mode: "signup" })}>{arabic ? "إنشاء حساب" : "Create account"}</Button>
            </div>}
          {authError.message && !formMethod && (
            <p role="alert" className="checkout-auth__error">
              {authError.message}
            </p>
          )}
          {!formMethod ? (
            this.renderProviderChoice(capability, arabic)
          ) : (
            <form className="checkout-auth__form" onSubmit={(event) => { event.preventDefault(); this.submit(); }}>
              {overlay.method === "phone_otp" ? (
                <MuiPhoneNumber
                  id="checkout-auth-phone"
                  required fullWidth autoComplete="tel"
                  variant="outlined"
                  defaultCountry={selectedCountry?.dial_code || this.props.store?.default_dial_code || "kw"}
                  onlyCountries={getCheckoutPhoneCountryOptions(this.props)}
                  excludeCountries={["il"]} countryCodeEditable={false} disableAreaCodes
                  label={arabic ? "رقم الهاتف" : "Phone"}
                  error={Boolean(authError.fields.phone)}
                  helperText={authError.fields.phone}
                  FormHelperTextProps={{ role: "alert" }}
                  value={this.state.identifier}
                  onChange={(identifier) => this.setState({ identifier, error: "" })}
                  disabled={this.state.verificationRequired || this.state.loading}
                />
              ) : <TextField
                id="checkout-auth-email"
                autoFocus
                fullWidth
                variant="outlined"
                autoComplete="email"
                type={overlay.method === "email" ? "email" : "tel"}
                error={Boolean(authError.fields.email)}
                helperText={authError.fields.email}
                FormHelperTextProps={{ role: "alert" }}
                label={
                  overlay.method === "email"
                    ? arabic
                      ? "البريد الإلكتروني"
                      : "Email"
                    : arabic
                      ? "رقم الهاتف"
                      : "Phone"
                }
                value={this.state.identifier}
                onChange={(event) =>
                  this.setState({ identifier: event.target.value, error: "" })
                }
                disabled={this.state.verificationRequired || this.state.loading}
              />}
              {!this.state.verificationRequired &&
              overlay.method === "email" ? (
                <TextField
                  id="checkout-auth-password"
                  fullWidth
                  variant="outlined"
                  autoComplete={signup ? "new-password" : "current-password"}
                  disabled={this.state.loading}
                  type="password"
                  label={arabic ? "كلمة المرور" : "Password"}
                  error={Boolean(authError.fields.password)}
                  helperText={authError.fields.password}
                  FormHelperTextProps={{ role: "alert" }}
                  value={this.state.password}
                  onChange={(event) =>
                    this.setState({ password: event.target.value, error: "" })
                  }
                />
              ) : this.state.verificationRequired ? (
                <TextField
                  id="checkout-auth-code"
                  fullWidth
                  variant="outlined"
                  autoFocus
                  autoComplete="one-time-code"
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                  label={arabic ? "رمز التحقق" : "Verification code"}
                  error={Boolean(authError.fields.code)}
                  helperText={authError.fields.code}
                  FormHelperTextProps={{ role: "alert" }}
                  value={this.state.code}
                  onChange={(event) =>
                    this.setState({
                      code: event.target.value.replace(/\D/g, ""),
                      error: "",
                    })
                  }
                />
              ) : null}
              {emailMethod && !signup && !verification && <Button className="checkout-auth__forgot" disabled={this.state.loading}
                onClick={() => this.props.onPasswordReset ? this.props.onPasswordReset(this.state.identifier)
                  : this.setState({ resettingPassword: true, password: "", error: "" })}>
                {arabic ? "نسيت كلمة المرور؟" : "Forgot password?"}
              </Button>}
              {signup && !verification && settings.enable_dot && <div className="checkout-auth__marketing">
                <FormControlLabel control={<Checkbox checked={this.state.optInMarketing} disabled={this.state.loading} onChange={(event) => this.setState({ optInMarketing: event.target.checked, contactChannel: "Email" })} />} label={arabic ? "أرغب في تلقي العروض والتحديثات (اختياري)" : "Send me offers and updates (optional)"} />
                {this.state.optInMarketing && <RadioGroup row aria-label={arabic ? "طريقة تلقي العروض" : "Offers contact method"} value={this.state.contactChannel} onChange={(event) => this.setState({ contactChannel: event.target.value })}>
                  <FormControlLabel value="Email" control={<Radio disabled={this.state.loading} />} label={arabic ? "البريد الإلكتروني" : "Email"} />
                  <FormControlLabel value="SMS" control={<Radio disabled={this.state.loading} />} label={arabic ? "رسائل نصية" : "SMS"} />
                </RadioGroup>}
              </div>}
              {authError.message && (
                <p role="alert" className="checkout-auth__error">{authError.message}</p>
              )}
              <StorefrontActionButton
                disabled={this.state.loading}
                type="submit"
              >
                {this.state.loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : this.state.verificationRequired ? (
                  arabic ? (
                    "تحقق"
                  ) : (
                    "Verify"
                  )
                ) : arabic ? (
                  overlay.method === "phone_otp"
                    ? "إرسال الرمز"
                    : "متابعة"
                ) : (
                  overlay.method === "phone_otp"
                    ? "Send code"
                    : "Continue"
                )}
              </StorefrontActionButton>
              {this.state.verificationRequired && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <StorefrontActionButton tone="secondary" disabled={this.state.loading || resendSeconds > 0}
                    onClick={() => this.submit({ resend: true })}>
                    {resendSeconds > 0
                      ? arabic ? `إعادة الإرسال خلال ${resendSeconds} ثانية` : `Resend in ${resendSeconds}s`
                      : arabic ? "إعادة إرسال الرمز" : "Resend code"}
                  </StorefrontActionButton>
                  <Button onClick={this.changeIdentifier} disabled={this.state.loading}>
                    {overlay.method === "phone_otp"
                      ? arabic ? "تغيير الرقم" : "Change number"
                      : arabic ? "تغيير البريد الإلكتروني" : "Change email"}
                  </Button>
                </div>
              )}
            </form>
          )}
          {formMethod && alternativeProviders.length > 0 && <div className="checkout-auth__alternatives">
            <p>{arabic ? "أو تابع باستخدام" : "Or continue with"}</p>
            {this.renderProviderChoice({ ...capability, providers: alternativeProviders }, arabic, true)}
          </div>}
          </>}
        </DialogContent>
        {!this.props.embedded && capability.guestAllowed && <DialogActions className="checkout-auth__footer">
            <Button className="checkout-auth__guest" onClick={this.props.close}>
              {arabic ? "المتابعة كضيف" : "Continue as guest"}
            </Button>
        </DialogActions>}
      </AuthContainer>
    );
  }
}

export default withRouter(
  connect(
    (state) => ({
      overlay: state.checkoutV2?.authOverlay,
      settings: state.store?.store?.settings || {},
      source: state.promise?.src || "web",
      user: state.auth?.user,
      slug: state.store?.store?.slug,
      language: state.store?.language || "english",
      store: state.store?.store || {},
      address: state.customers?.address || {},
      countries: state.store?.store?.all_countries || state.store?.store?.countries || [],
    }),
    (dispatch) => ({
      open: (options) => dispatch(openCheckoutV2Auth(options)),
      close: () => dispatch(closeCheckoutV2Auth()),
      setUser: (token, history) => dispatch(setUser(token, history)),
    }),
  )(CheckoutAuthOverlay),
);
